import { useState } from 'react';
import { Copy, Check, ExternalLink } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { cn } from '@/client/lib/utils';

/**
 * Reusable detail view components for entity detail pages.
 * Use these to build consistent detail views across the app.
 */

// ============================================================================
// DetailCard - A section container for grouping related fields
// ============================================================================

interface DetailCardProps {
  title: string;
  children: React.ReactNode;
  className?: string;
  actions?: React.ReactNode;
}

export function DetailCard({ title, children, className, actions }: DetailCardProps) {
  return (
    <div className={cn('rounded-lg border bg-card min-w-0', className)}>
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-medium text-sm">{title}</h3>
        {actions}
      </div>
      <div className="p-4 overflow-hidden">{children}</div>
    </div>
  );
}

// ============================================================================
// DetailGrid - A responsive grid for multiple DetailCards
// ============================================================================

interface DetailGridProps {
  children: React.ReactNode;
  columns?: 1 | 2 | 3;
  className?: string;
}

export function DetailGrid({ children, columns = 2, className }: DetailGridProps) {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 lg:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',
  };

  return (
    <div className={cn('grid gap-4', gridCols[columns], className)}>
      {children}
    </div>
  );
}

// ============================================================================
// DetailRow - A key-value pair row within a DetailCard
// ============================================================================

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  className?: string;
}

export function DetailRow({ label, value, mono = false, className }: DetailRowProps) {
  return (
    <div className={cn('flex justify-between items-start py-2 border-b border-border/50 last:border-0', className)}>
      <span className="text-muted-foreground text-sm shrink-0 mr-4">{label}</span>
      <span className={cn('text-sm text-right', mono && 'font-mono')}>{value ?? '-'}</span>
    </div>
  );
}

// ============================================================================
// DetailList - Vertical list layout (label above value)
// ============================================================================

interface DetailListProps {
  children: React.ReactNode;
  className?: string;
}

export function DetailList({ children, className }: DetailListProps) {
  return <div className={cn('space-y-0', className)}>{children}</div>;
}

// ============================================================================
// CopyableText - Text with click-to-copy functionality
// ============================================================================

interface CopyableTextProps {
  text: string;
  truncate?: boolean;
  maxLength?: number;
  className?: string;
}

export function CopyableText({ text, truncate = false, maxLength = 30, className }: CopyableTextProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const displayText = truncate && text.length > maxLength
    ? `${text.slice(0, maxLength - 3)}...`
    : text;

  return (
    <button
      onClick={handleCopy}
      className={cn(
        'inline-flex items-center gap-1.5 font-mono text-sm hover:text-primary cursor-pointer group text-right',
        className
      )}
      title={truncate && text.length > maxLength ? text : 'Click to copy'}
    >
      <span>{displayText}</span>
      {copied ? (
        <Check className="h-3 w-3 text-green-500 shrink-0" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground shrink-0" />
      )}
    </button>
  );
}

// ============================================================================
// BooleanBadge - Visual indicator for boolean values
// ============================================================================

interface BooleanBadgeProps {
  value: boolean | null | undefined;
  trueLabel?: string;
  falseLabel?: string;
}

export function BooleanBadge({ value, trueLabel = 'Yes', falseLabel = 'No' }: BooleanBadgeProps) {
  if (value === null || value === undefined) {
    return <span className="text-sm text-muted-foreground">-</span>;
  }

  return (
    <span className={cn(
      'text-sm',
      value ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
    )}>
      {value ? trueLabel : falseLabel}
    </span>
  );
}

// ============================================================================
// DetailLink - Internal navigation link
// ============================================================================

interface DetailLinkProps {
  to: string;
  params?: Record<string, string>;
  children: React.ReactNode;
  className?: string;
}

export function DetailLink({ to, params, children, className }: DetailLinkProps) {
  return (
    <Link
      to={to}
      params={params}
      className={cn('text-sm text-primary hover:underline', className)}
    >
      {children}
    </Link>
  );
}

// ============================================================================
// ExternalLink - Opens in new tab
// ============================================================================

interface ExternalLinkButtonProps {
  href: string;
  children: React.ReactNode;
  className?: string;
}

export function ExternalLinkButton({ href, children, className }: ExternalLinkButtonProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn('inline-flex items-center gap-1 text-sm text-primary hover:underline', className)}
    >
      {children}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

// ============================================================================
// DetailHeader - Page header with back button and title
// ============================================================================

interface DetailHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
  backTo: string;
  backLabel?: string;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
}

export function DetailHeader({ title, subtitle, backTo, backLabel, badge, actions }: DetailHeaderProps) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div className="flex items-start gap-4">
        <Link
          to={backTo}
          className="mt-1 p-2 -ml-2 rounded-md hover:bg-muted transition-colors"
          title={backLabel || 'Go back'}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
        </Link>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">{title}</h1>
            {badge}
          </div>
          {subtitle && (
            <div className="text-sm text-muted-foreground mt-1">{subtitle}</div>
          )}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// ============================================================================
// Utility functions
// ============================================================================

export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString();
}

export function formatTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleTimeString();
}
